import { SkipCache } from '@warp-drive/core/types/request';
import type { Handler, NextFn } from '@warp-drive/core/request';
import type { RequestContext, ImmutableRequestInfo } from '@warp-drive/core/types/request';
import type { Store } from '@warp-drive/core';
import type { ResourceKey } from '@warp-drive/core/types/identifier';
import type {
  DemoProvider,
  DemoScenarioResolved,
  JsonApiDoc,
  JsonApiResource,
  RequestMatcher,
} from '../demo/types.ts';

const MUTATION_OPS = new Set(['createRecord', 'updateRecord', 'deleteRecord']);
const SINGLE_READ_OPS = new Set(['findRecord', 'queryRecord', 'findBelongsTo']);

type ResourceKeyish = { type: string; id: string | null };

/**
 * Demo-mode request handler. Placed FIRST in the request-manager chain (before
 * `LegacyNetworkHandler`, behind only `CacheHandler`), so when a demo session
 * is active it can intercept BOTH legacy adapter ops (findRecord/query/save…)
 * and builder requests (store.request) and serve scripted fixtures — leaving
 * routes and components demo-blind.
 *
 * It only ever short-circuits requests carrying `cacheOptions[SkipCache]` (the
 * flag every tio legacy op and builder sets); everything else, and every
 * request when demo mode is inactive or unmatched, falls through to `next()`
 * unchanged. The returned value is a plain record/array/object — the request
 * manager wraps it into `{ content }`, which legacy store methods and builder
 * callers both unwrap.
 */
export class TioDemoHandler implements Handler {
  constructor(private demo: DemoProvider) {}

  async request<T>(context: RequestContext, next: NextFn<T>) {
    const { request } = context;

    // Only intercept SkipCache requests while demo mode is active. This mirrors
    // CacheHandler's own guard and guarantees we never shadow cache-managed reads.
    if (!this.demo.isActive() || !request.cacheOptions?.[SkipCache]) {
      return next(request);
    }

    const resolved = this.demo.resolve(request);
    const store = request.store as Store | undefined;
    if (!resolved || !store) {
      return next(request);
    }

    // Seed shared fixtures once so later peekRecord/peekAll reads resolve.
    this.seed(store, resolved);

    const op = request.op;

    // A resolved scenario means the session is showing demo data, so a
    // mutation must NEVER reach the real API — echo the targeted record
    // (createRecord has already added an optimistic record to the store) and
    // drop the save. Handled categorically, before matcher lookup, because
    // mutation ops are not part of the read-matcher vocabulary.
    if (op && MUTATION_OPS.has(op)) {
      const target = this.resourceKey(request);
      return (target?.id ? store.peekRecord(target.type, target.id) : null) as T;
    }

    const hit = resolved.matchers.find((entry) => this.matches(entry.matcher, request));
    if (!hit) {
      // Unmatched read: fall through to the network so unscripted pages still
      // load real data during a partial demo.
      return next(request);
    }

    // Builder request: return the raw fixture; the manager wraps it as
    // `{ content }` and the caller reads `.content`.
    if (hit.matcher.kind === 'builder') {
      return hit.content as T;
    }

    const key = this.resourceKey(request);
    const docs = hit.docs ?? [];

    if (op && SINGLE_READ_OPS.has(op)) {
      // Single-record read: echo the requested id (when the op carries one) so
      // the store resolves the record under the identifier it asked for.
      const doc = this.withEchoedId(docs[0], key);
      return (doc ? this.pushDoc(store, doc) : null) as T;
    }

    // Collection read (query/findAll): push each doc, return the flattened
    // array of materialized records.
    const records: unknown[] = [];
    for (const doc of docs) {
      const pushed = this.pushDoc(store, doc);
      if (Array.isArray(pushed)) {
        records.push(...pushed);
      } else if (pushed != null) {
        records.push(pushed);
      }
    }
    return records as T;
  }

  private matches(matcher: RequestMatcher, request: ImmutableRequestInfo): boolean {
    if (matcher.kind === 'builder') {
      const method = (request.method ?? 'GET').toUpperCase();
      if (matcher.method && matcher.method.toUpperCase() !== method) {
        return false;
      }
      const url = request.url ?? '';
      if (matcher.urlMatches) {
        return matcher.urlMatches(url);
      }
      return matcher.urlIncludes ? url.includes(matcher.urlIncludes) : false;
    }

    if (request.op !== matcher.op) {
      return false;
    }
    if (this.requestType(request) !== matcher.type) {
      return false;
    }
    if (matcher.where) {
      const data = request.data as { query?: Record<string, unknown> } | undefined;
      return matcher.where(data?.query);
    }
    return true;
  }

  private requestType(request: ImmutableRequestInfo): string | undefined {
    const key = this.resourceKey(request);
    if (key) {
      return key.type;
    }
    const data = request.data as { type?: string } | undefined;
    return data?.type;
  }

  private resourceKey(request: ImmutableRequestInfo): ResourceKeyish | undefined {
    const fromRecords = request.records?.[0] as ResourceKey | undefined;
    if (fromRecords) {
      return { type: fromRecords.type, id: fromRecords.id ?? null };
    }
    const data = request.data as { record?: ResourceKey } | undefined;
    const record = data?.record;
    return record ? { type: record.type, id: record.id ?? null } : undefined;
  }

  private withEchoedId(
    doc: JsonApiDoc | undefined,
    key: ResourceKeyish | undefined
  ): JsonApiDoc | undefined {
    if (!doc) {
      return undefined;
    }
    if (!key?.id || Array.isArray(doc.data) || !doc.data) {
      return doc;
    }
    const originalId = doc.data.id;
    if (originalId === key.id) {
      return doc;
    }
    // The op asked for a specific id (often a live session id). Rewrite the
    // fixture's primary resource to that id AND repoint every linkage that
    // referenced the original id (in `data` and `included`), so sideloaded
    // back-references (e.g. pslf-profile -> employee) still resolve.
    const clone = structuredClone(doc);
    const type = (clone.data as JsonApiResource).type;
    this.rewriteId(clone, type, originalId, key.id);
    return clone;
  }

  private rewriteId(doc: JsonApiDoc, type: string, oldId: string, newId: string): void {
    const fixResource = (resource: JsonApiResource) => {
      if (resource.type === type && resource.id === oldId) {
        resource.id = newId;
      }
      for (const relationship of Object.values(resource.relationships ?? {})) {
        const linkage = relationship.data;
        if (Array.isArray(linkage)) {
          for (const ref of linkage) {
            if (ref.type === type && ref.id === oldId) {
              ref.id = newId;
            }
          }
        } else if (linkage && linkage.type === type && linkage.id === oldId) {
          linkage.id = newId;
        }
      }
    };

    if (doc.data && !Array.isArray(doc.data)) {
      fixResource(doc.data);
    }
    for (const resource of doc.included ?? []) {
      fixResource(resource);
    }
  }

  private seed(store: Store, resolved: DemoScenarioResolved): void {
    if (!resolved.seed) {
      return;
    }
    for (const doc of resolved.seed) {
      this.pushDoc(store, doc);
    }
  }

  // Push a JSON:API doc into the store. Cast the push method structurally so
  // we don't have to import warp-drive's internal document types (and stay
  // clear of `any`); push returns a record for a single doc, an array for a
  // collection.
  //
  // Always push a DEEP CLONE: `store.push` stamps identity (`lid`) onto the
  // resource objects in the document, but fixtures are shared module-level
  // constants reused across requests (and tests). Pushing them by reference
  // mutates/freezes them, so a later push of the same fixture throws
  // ("Cannot assign to read only property 'lid'"). Cloning keeps fixtures pristine.
  private pushDoc(store: Store, doc: JsonApiDoc): unknown {
    const clone = structuredClone(doc);
    return (store.push as unknown as (document: JsonApiDoc) => unknown)(clone);
  }
}
